/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 *
 * useCampaignWatchdog — Pseudo-backend "auto-post" engine.
 *
 * Runs every 60 seconds (and on mount) to check for PLANNED campaigns whose
 * scheduled date+time has passed, then transitions them to ACTIVE and creates
 * a system-level notification + audit log.
 */

import { useEffect, useRef, useCallback } from 'react';
import { getRepository } from '../config/repository';
import { NotificationType } from '../domain/entities';
import { CampaignStatus } from '../modules/aggregator/types';

const WATCHDOG_INTERVAL_MS = 60_000; // 60 seconds

/**
 * Combine a campaign's `validFrom` (Date as Unix ms) and `startTime` ("HH:mm")
 * into a single Unix timestamp.  If no startTime, defaults to midnight (00:00).
 */
function getScheduledTimestamp(validFrom: number, startTime?: string): number {
  const d = new Date(validFrom);
  if (startTime && /^\d{2}:\d{2}$/.test(startTime)) {
    const [h, m] = startTime.split(':').map(Number);
    d.setHours(h, m, 0, 0);
  } else {
    d.setHours(0, 0, 0, 0);
  }
  return d.getTime();
}

export function useCampaignWatchdog() {
  const running = useRef(false);

  const tick = useCallback(async () => {
    if (running.current) return; // prevent overlapping runs
    running.current = true;

    try {
      const repo = getRepository();
      const campaigns = await repo.getCampaigns();
      const now = Date.now();

      for (const camp of campaigns) {
        if (camp.status !== CampaignStatus.PLANNED) continue;

        const scheduledTs = getScheduledTimestamp(camp.validFrom, camp.startTime);
        if (now < scheduledTs) continue;

        // ── Transition PLANNED → ACTIVE ──
        const updated = { ...camp, status: CampaignStatus.ACTIVE };
        await repo.saveCampaign(updated);

        // ── System audit log ──
        await repo.saveSystemLog({
          id: crypto.randomUUID(),
          userId: 'system',
          userName: 'Watchdog Engine',
          action: `Auto-posted campaign "${camp.name}" — transitioned from PLANNED to ACTIVE.`,
          timestamp: Date.now(),
          moduleId: 'aggregator',
        });

        // ── System notification (broadcast to all admins via recipientId='*') ──
        await repo.saveNotification({
          id: crypto.randomUUID(),
          recipientId: '*',         // wildcard — all users see it
          actorId: 'system',
          type: NotificationType.INFO,
          title: 'Campaign is now LIVE',
          message: `Campaign "${camp.name}" is now LIVE. It was auto-posted by the scheduled Watchdog engine.`,
          targetPath: '/apps/aggregator/management',
          isRead: false,
          createdAt: Date.now(),
        });
      }
    } catch {
      // Silently swallow — watchdog is best-effort in local-first mode
    } finally {
      running.current = false;
    }
  }, []);

  useEffect(() => {
    // Run immediately on mount
    tick();
    // Then run every 60s
    const id = setInterval(tick, WATCHDOG_INTERVAL_MS);
    return () => clearInterval(id);
  }, [tick]);
}
